import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, User, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import useBrandAsset from "@/lib/useBrandAsset";
import { useCartCount } from "@/lib/cart";

// Private roles trigger the Dashboard / Training surface in the nav
const PRIVATE_ROLES = new Set(["affiliate", "driver", "partner", "franchisee", "trainer",
  "teacher", "affiliate_trainer", "franchisee_trainer", "training_admin", "admin", "master_admin"]);

const PUBLIC_NAV = [
  { to: "/",                                label: "Home" },
  { to: "/shop",                            label: "Shop" },
  { to: "/collections/lifted-gear",         label: "Lifted Gear" },
  { to: "/collections/lifted-accessories",  label: "Lifted Accessories" },
  { to: "/lifted-monthly-box",              label: "Monthly Box" },
  { to: "/lifted-marketing",                label: "Marketing" },
  { to: "/lifted-views-news",               label: "Lifted Views News" },
  { to: "/supplies",                        label: "Lifted Supplies" },
  { to: "/affiliate-zone",                  label: "Affiliate Zone" },
  { to: "/ecosystem",                       label: "Lifted Ecosystem" },
  { to: "/about",                           label: "About" },
  { to: "/contact",                         label: "Contact" },
];

export default function PublicHeader() {
  const { user } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const isHome = loc.pathname === "/";
  const showDashboard = user && (user.roles || []).some((r) => PRIVATE_ROLES.has(r));
  // Phase 22 — live cart count badge. Updates instantly on add/remove without
  // a page reload, via the `cart:updated` CustomEvent broadcast in lib/cart.js
  const cartCount = useCartCount();
  const cartBadge = cartCount > 0 ? (cartCount > 99 ? "99+" : String(cartCount)) : null;

  // Brand assets — resolved from the Brand Asset Library by tag
  const xMascot = useBrandAsset("mascot.brand-ambassador", { fallback: "mascot.rocket" });
  const wordmark = useBrandAsset("wordmark.primary");  // eslint-disable-line no-unused-vars

  return (
    <header data-testid="public-header" className={`sticky top-0 z-40 backdrop-blur-md ${isHome ? "bg-black/40" : "bg-black/70"} border-b border-white/5`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="public-header-brand">
          {xMascot.url && <img src={xMascot.url} alt="Lifted" className="h-8 w-8" />}
          <span className="font-anton tracking-[0.18em] text-lifted-text uppercase text-base sm:text-lg whitespace-nowrap">LiftedAtTheMoment<span className="text-lifted-green text-xs align-top ml-0.5">™️🚀</span></span>
        </Link>
        <nav className="hidden xl:flex items-center gap-6 text-sm font-bebas tracking-[0.18em] uppercase">
          {PUBLIC_NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`public-nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
                  className={`hover:text-lifted-green transition-colors ${loc.pathname === n.to ? "text-lifted-green" : "text-lifted-text"}`}>{n.label}</Link>
          ))}
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          {showDashboard && (
            <Link to="/dashboard" className="btn-ghost !py-2 !px-3 !text-[0.65rem] hidden sm:inline-flex" data-testid="public-header-dashboard">
              Dashboard
            </Link>
          )}
          {user ? (
            <Link to="/account" className="btn-ghost !py-2 !px-3 !text-[0.65rem]" data-testid="public-header-account"><User className="h-3 w-3" /> Account</Link>
          ) : (
            <Link to="/auth/login" className="btn-ghost !py-2 !px-3 !text-[0.65rem]" data-testid="public-header-signin">Sign in</Link>
          )}
          <Link to="/cart" className="btn-lifted !py-2 !px-3 !text-[0.65rem] relative" data-testid="public-header-cart"><ShoppingBag className="h-3 w-3" /> Cart
            {cartBadge && (
              <span
                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border border-black"
                data-testid="public-header-cart-badge"
              >
                {cartBadge}
              </span>
            )}
          </Link>
          <button onClick={() => setOpen(!open)} className="xl:hidden ml-1 btn-ghost !p-2" data-testid="public-header-mobile-toggle" aria-label="Menu">
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="xl:hidden border-t border-white/5 px-4 py-3 space-y-1 bg-black/95" data-testid="public-header-mobile-drawer">
          {PUBLIC_NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block py-2 text-sm text-lifted-text hover:text-lifted-green font-bebas tracking-[0.18em] uppercase"
                  data-testid={`public-nav-mobile-${n.label.toLowerCase().replace(/\s+/g,'-')}`}>
              {n.label}
            </Link>
          ))}
          {showDashboard && (
            <Link to="/dashboard" onClick={() => setOpen(false)} className="block py-2 text-sm text-lifted-green font-bebas tracking-[0.18em] uppercase border-t border-white/5 mt-2 pt-3">
              Dashboard · Training
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
